import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const ApricotPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Apricot Success: Perfect pH for Premium Fruit"
        description="Research-backed pH management for profitable apricot production. Learn how water quality affects your apricot orchard yield and quality."
        canonicalPath="/apricot"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/1263986/pexels-photo-1263986.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Apricot orchard with ripe orange apricots on trees"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-900/80 via-amber-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🍑</span>
              <span>Premium Stone Fruit Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Apricot Success:
              <span className="block text-orange-300">Perfect pH for Premium Fruit</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management for profitable apricot production
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-orange-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-orange-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🍑</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Soil pH isn't just a number—it governs nutrient availability, root health, and ultimately fruit yield and quality.</strong> Your irrigation water could be quietly pushing your soil pH out of the optimal range season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped stone fruit growers optimize their water quality for 40+ years. This guide explains why your irrigation water pH matters for apricots, what research shows, and how sulfur burners can protect your soil, enhance your fruit quality, and maximize your profits.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Optimal pH Range for Apricots */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Optimal pH Range for Apricots
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6">
                "Apricot trees flourish in soils with pH 6.0–7.0. This ensures essential nutrients like nitrogen, phosphorus, potassium, calcium, magnesium, and trace elements are accessible to the roots."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-orange-600">
                  Source: Multiple Agricultural Research Sources
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Maintaining this pH sweet spot ensures your apricot trees can access all the nutrients they need for healthy growth and premium fruit production.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: pH Risks - Too High or Too Low */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">⚠️</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: pH Risks — Too High or Too Low
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 md:p-8 border border-red-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-red-500 pl-6">
                "Stonefruit will grow best within a soil pH range of 6.0–6.7. At lower pH, root growth and tree health are adversely affected by aluminum and manganese toxicity. At higher pH, trace element deficiencies can be induced."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-red-600" size={16} />
                <span className="text-sm font-medium text-red-600">
                  Source: Hill Laboratories — Stone Fruit Soil Management
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ Acidic Risk (pH &lt; 6.0):</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Aluminum toxicity impairs root development</li>
                    <li>• Manganese toxicity stunts growth</li>
                    <li>• Poor nutrient uptake efficiency</li>
                    <li>• Reduced tree vigor and fruit set</li>
                  </ul>
                </div>
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ Alkaline Danger (pH &gt; 7.0):</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Iron deficiency causes chlorosis</li>
                    <li>• Zinc and manganese become unavailable</li>
                    <li>• Poor fruit set and development</li>
                    <li>• Yellowing leaves with green veins</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">High-bicarbonate irrigation water can gradually push your soil pH above 7.0, triggering nutrient deficiencies that hurt fruit quality and yield.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Understanding pH's Impact on Nutrients */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🧪</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Understanding pH's Impact on Nutrients
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Most macro- and micronutrients are most available in the pH 6–7 zone. Outside this range, essential elements become chemically locked away, even when present in adequate amounts in the soil.
              </p>

              <div className="bg-white rounded-lg p-6 mb-6 shadow-md">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Toxicity Thresholds to Avoid:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-2">Aluminum Toxicity</h4>
                    <p className="text-sm text-gray-700">Becomes toxic below pH 5.0 — a condition apricot trees must avoid</p>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                    <h4 className="font-semibold text-red-800 mb-2">Manganese Toxicity</h4>
                    <p className="text-sm text-gray-700">Appears below pH 5.6 — another critical threshold for stone fruit</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Extreme pH affects soil structure and water retention, compromising root function and stress resistance — making pH management critical for orchard success.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Agronomic Best Practices */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">✅</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: Agronomic Best Practices
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Soil Testing Before Planting</h3>
                  <p className="text-gray-700 mb-4">
                    Always conduct a comprehensive soil analysis for pH, cation exchange capacity (CEC), nutrient levels, and structure. This ensures tailored amendments, avoiding broad-brush fixes.
                  </p>
                  <div className="bg-green-100 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium">
                      🔬 UNH recommends testing every new orchard site, especially for stonefruit like apricots
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Adjusting pH Appropriately</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-800 mb-2">Low pH (&lt;6.0)</h4>
                      <p className="text-sm text-gray-700">Add agricultural lime or dolomite (dolomite adds Mg and K), ideally applied in winter</p>
                    </div>
                    <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                      <h4 className="font-semibold text-orange-800 mb-2">High pH (&gt;7.0)</h4>
                      <p className="text-sm text-gray-700">Incorporate acidifying agents like elemental sulfur or acidifying fertilizers</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Soil Preparation & Amendments</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Till deeply (50–70 cm/20–27″) to remove weeds and improve root environment</li>
                    <li>• Mix in organic matter—compost, peat, well-aged manure—to enhance structure</li>
                    <li>• Plant in full sun, well-drained loam or sandy loam</li>
                    <li>• Avoid heavy clay or waterlogged soils</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 5: pH-Driven Yield & Quality Benefits */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: pH-Driven Yield & Quality Benefits
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <p className="text-gray-700 mb-6 leading-relaxed">
                While many lab studies on apricot fruit pH are limited, orchard experience validates these benefits when soil pH is properly managed:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Fruit Quality Improvements</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Higher soluble solids (°Brix)</li>
                    <li>• Better acidity balance</li>
                    <li>• Improved color development</li>
                    <li>• Enhanced firmness and shelf life</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-md border border-purple-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Tree Performance</h3>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li>• Balanced pH supports healthy root systems</li>
                    <li>• Larger, more consistent fruit size</li>
                    <li>• Better stress resistance</li>
                    <li>• Improved overall tree vigor</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Research Note:</span>
                </div>
                <p className="text-gray-700">Biostimulants (like protein hydrolysates) can boost yield ~54% and fruit size +8%, but perform best when foundational soil conditions (including pH) are properly managed.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 6: Expert Insights & Quick Checklist */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💡</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                6: Expert Insights & Quick Checklist
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-teal-200">
              <div className="space-y-6">
                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Expert Quotes</h3>
                  <div className="space-y-4">
                    <blockquote className="text-gray-700 italic border-l-4 border-teal-500 pl-4">
                      "Apricot trees need a soil pH between 6.0–7.0... Check your soil's pH before you dig." — Herbeins Garden Center
                    </blockquote>
                    <blockquote className="text-gray-700 italic border-l-4 border-teal-500 pl-4">
                      "Apricots thrive in deep, well‑drained silt or sandy loam soil. A pH of 6.0–6.5 is recommended." — HerbaZest
                    </blockquote>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-md">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick pH Checklist for Apricot Growers</h3>
                  <div className="space-y-3">
                    {[
                      { step: '1', action: 'Test soil (6″ depth) for pH, nutrients, CEC' },
                      { step: '2', action: 'Adjust pH to 6.2–6.8 (lime if ≤6.0; sulfur if ≥7.2)' },
                      { step: '3', action: 'Improve soil with organic matter and deep tilling' },
                      { step: '4', action: 'Plant in full sun, well-drained loam' },
                      { step: '5', action: 'Monitor & maintain, retesting soil every 2–3 years' },
                      { step: '6', action: 'Watch for yellowing leaves or weak growth—correct promptly' }
                    ].map((item, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-teal-600 font-bold text-sm">{item.step}</span>
                        </div>
                        <span className="text-gray-700">{item.action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-lg p-6 mt-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Apricots:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents nutrient lockout and toxicity',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in stone fruit'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Apricots" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ApricotPage;